import { cn } from "@/lib/utils";
import EyebrowLabel from "@/components/ui/EyebrowLabel";

/** A single segment of the heading title. Mark highlight=true to apply gradient styling. */
interface TitleSegment {
  text: string;
  highlight?: boolean;
}

interface SectionHeadingProps {
  eyebrow?: React.ReactNode;
  /**
   * Pass a plain string OR an array of { text, highlight } segments.
   *
   * String form:
   *   title="Deploy nodes at scale"
   *
   * Segment form (gradient on a word or phrase):
   *   title={[{ text: "Deploy nodes " }, { text: "at scale", highlight: true }]}
   */
  title: string | TitleSegment[];
  subtitle?: string;
  /** "left" | "center" (default: "center") */
  align?: "left" | "center";
  className?: string;
}

export default function SectionHeading({
  eyebrow,
  title,
  subtitle,
  align = "center",
  className,
}: SectionHeadingProps) {
  const isCenter = align === "center";

  const segments: TitleSegment[] =
    typeof title === "string" ? [{ text: title }] : title;

  return (
    <div
      className={cn(
        "max-w-3xl",
        isCenter ? "mx-auto text-center" : "text-left",
        className,
      )}
    >
      {eyebrow && (
        <EyebrowLabel align={isCenter ? "center" : "left"} className="mb-3">
          {eyebrow}
        </EyebrowLabel>
      )}

      <h2 className="text-3xl font-bold text-text-primary sm:text-4xl">
        {segments.map((seg, i) =>
          seg.highlight ? (
            <span key={i} className="gradient-text">
              {seg.text}
            </span>
          ) : (
            <span key={i}>{seg.text}</span>
          ),
        )}
      </h2>

      {subtitle && (
        <p className="mt-4 text-lg leading-relaxed text-text-secondary">{subtitle}</p>
      )}
    </div>
  );
}
